/**
 * Last-known user profile + organization, so an installed app can boot with
 * no service. The Supabase session itself is already in localStorage; the
 * `users` row that AuthProvider fetches on every start was not, and without
 * it every route gated on the profile bounced to the login screen offline.
 */
import type { Organization, User } from '@/types'

const KEY = 'opsyri:profile-cache'

interface CachedProfile { userId: string; user: User; organization: Organization | null; at: number }

export function saveProfileCache(userId: string, user: User, organization: Organization | null): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ userId, user, organization, at: Date.now() } satisfies CachedProfile))
  } catch { /* ignore */ }
}

export function loadProfileCache(userId: string): { user: User; organization: Organization | null } | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const c = JSON.parse(raw) as Partial<CachedProfile>
    if (c?.userId !== userId || !c.user) return null
    return { user: c.user, organization: c.organization ?? null }
  } catch {
    return null
  }
}

export function clearProfileCache(): void {
  try { localStorage.removeItem(KEY) } catch { /* ignore */ }
}
